import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onReady"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback } from 'react';
import { CardCvcElement as StripeCardCVCElement, CardExpiryElement as StripeCardExpiryElement, CardNumberElement as StripeCardNumberElement } from '@stripe/react-stripe-js';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { ErrorMessage } from '@atlaskit/form';
import AKTextField from '@atlaskit/textfield';
import * as FieldNames from '../../../common/constants/field-names';
import { useCardFieldUpdate } from '../../../controllers/hooks/use-credit-card-fields';
import { useReactFieldState } from '../../../controllers/reactive-field-state';
import { messages } from './messages';
import { ErrorWrapper, Field, FormGrid, inputOptions, Label, LabelSpan } from './styled';
var FieldError = function FieldError(_ref) {
  var error = _ref.error,
    id = _ref.id;
  return error && typeof error === 'string' ? /*#__PURE__*/React.createElement(ErrorWrapper, {
    id: id,
    role: "alert"
  }, /*#__PURE__*/React.createElement(ErrorMessage, null, error)) : null;
};
var ReactiveField = function ReactiveField(_ref2) {
  var name = _ref2.name,
    children = _ref2.children,
    onReady = _ref2.onReady;
  var _useReactFieldState = useReactFieldState({
      name: name,
      onReady: onReady
    }),
    state = _useReactFieldState.state,
    callbacks = _useReactFieldState.callbacks;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
    focused: state.focused,
    error: !!state.error,
    "data-testid": "stripe-cc-".concat(name),
    "aria-describedby": "error-".concat(name)
  }, children(callbacks)), /*#__PURE__*/React.createElement(FieldError, {
    id: "error-".concat(name),
    error: state.error !== undefined ? state.error : false
  }));
};
export var NameField = function NameField() {
  var intl = useIntl();
  var onNameChange = useCardFieldUpdate(FieldNames.NAME, function (e) {
    return e.currentTarget.value;
  });
  var _useReactFieldState2 = useReactFieldState({
      name: FieldNames.NAME
    }),
    state = _useReactFieldState2.state,
    _useReactFieldState2$ = _useReactFieldState2.callbacks,
    onReady = _useReactFieldState2$.onReady,
    callbacks = _objectWithoutProperties(_useReactFieldState2$, _excluded);
  var onChange = useCallback(function (e) {
    onNameChange(e);
    // This ensures the name error is cleared when the name is changed
    callbacks.onChange({
      error: undefined
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onNameChange, callbacks.onChange]);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    React.createElement(Label, {
      style: {
        gridArea: FieldNames.NAME
      }
    }, /*#__PURE__*/React.createElement(LabelSpan, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.fieldName)), /*#__PURE__*/React.createElement(AKTextField, _extends({
      name: "cardholderName",
      "aria-label": intl.formatMessage(messages.labelCardholderName)
    }, callbacks, {
      onChange: onChange,
      testId: "stripe-cc-".concat(FieldNames.NAME),
      ref: onReady,
      isInvalid: !!state.error,
      "aria-describedby": "error-".concat(FieldNames.NAME)
    })), /*#__PURE__*/React.createElement(FieldError, {
      id: "error-".concat(FieldNames.NAME),
      error: state.error !== undefined ? state.error : false
    }))
  );
};
export var NumberField = function NumberField(_ref3) {
  var _ref3$autoFocus = _ref3.autoFocus,
    autoFocus = _ref3$autoFocus === void 0 ? false : _ref3$autoFocus;
  var onFirstFieldReady = useCallback(function (field) {
    if (!autoFocus) {
      return;
    }
    field.focus();
  }, [autoFocus]);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    React.createElement(Label, {
      style: {
        gridArea: FieldNames.NUMBER
      }
    }, /*#__PURE__*/React.createElement(LabelSpan, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.fieldCardNumber)), /*#__PURE__*/React.createElement(ReactiveField, {
      name: FieldNames.NUMBER,
      onReady: onFirstFieldReady
    }, function (callbacks) {
      return /*#__PURE__*/React.createElement(StripeCardNumberElement, _extends({
        options: _objectSpread(_objectSpread({}, inputOptions), {}, {
          showIcon: true
        })
      }, callbacks));
    }))
  );
};
export var ExpiryField = function ExpiryField() {
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    React.createElement(Label, {
      style: {
        gridArea: FieldNames.EXPIRY
      }
    }, /*#__PURE__*/React.createElement(LabelSpan, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.fieldExpirationDate)), /*#__PURE__*/React.createElement(ReactiveField, {
      name: FieldNames.EXPIRY
    }, function (callbacks) {
      return /*#__PURE__*/React.createElement(StripeCardExpiryElement, _extends({
        options: inputOptions
      }, callbacks));
    }))
  );
};
export var CVCField = function CVCField() {
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    React.createElement(Label, {
      style: {
        gridArea: FieldNames.CVC
      }
    }, /*#__PURE__*/React.createElement(LabelSpan, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.fieldCvc)), /*#__PURE__*/React.createElement(ReactiveField, {
      name: FieldNames.CVC
    }, function (callbacks) {
      return /*#__PURE__*/React.createElement(StripeCardCVCElement, _extends({
        options: inputOptions
      }, callbacks));
    }))
  );
};
export var Form = function Form(_ref4) {
  var className = _ref4.className,
    autoFocus = _ref4.autoFocus;
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    React.createElement(FormGrid, {
      className: className
    }, /*#__PURE__*/React.createElement(NumberField, {
      autoFocus: autoFocus
    }), /*#__PURE__*/React.createElement(NameField, null), /*#__PURE__*/React.createElement(ExpiryField, null), /*#__PURE__*/React.createElement(CVCField, null))
  );
};